/**
 * Plan complexe, mode « Équation » : az² + bz + c = 0 à coefficients réels
 * (entiers). Δ, racines exactes posées sur la figure (conjuguées quand
 * Δ < 0), somme, produit et factorisation.
 */

import { useState } from 'react';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, Stepper } from '../../ui/controls';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { quadTex, solveQuadratic, vieta } from './complex';
import {
  Formula,
  LABEL_MARGIN,
  Live,
  Plane,
  PointMark,
  RES_COLOR,
  SOFT,
  StepList,
  frame,
  inView,
  px,
} from './parts';

const show = (v: number) => String(v).replace('-', '−');

export function EquationMode({ onSend, sendCost }: ToolProps) {
  const [a, setA] = useState(1);
  const [b, setB] = useState(-2);
  const [c, setC] = useState(5);
  const sol = solveQuadratic(a, b, c);
  const extra = vieta(a, b, c);
  const xs = -b / (2 * a);
  const names = sol.roots.length === 1 ? ['z₀'] : ['z₁', 'z₂'];
  const out = sol.roots.some((z) => !inView(z, frame, LABEL_MARGIN));
  const plain = quadTex(a, b, c).replace('^2', '²').replace(/-/g, '−');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-3">
        <Stepper
          label="a"
          value={a}
          min={-5}
          max={5}
          format={show}
          // a ≠ 0 : on saute le zéro
          onChange={(v) => setA(v === 0 ? (a > 0 ? -1 : 1) : v)}
        />
        <Stepper label="b" value={b} min={-10} max={10} format={show} onChange={setB} />
        <Stepper label="c" value={c} min={-10} max={10} format={show} onChange={setC} />
      </div>
      <Formula tex={quadTex(a, b, c)} className="text-center text-lg" />

      <Plane label={`Les solutions de ${plain} dans le plan complexe`}>
        {inView({ a: xs, b: 0 }) && (
          <line
            x1={frame.tx(xs)}
            y1={0}
            x2={frame.tx(xs)}
            y2={frame.size}
            stroke={SOFT}
            strokeDasharray="4 5"
            aria-hidden
          />
        )}
        {sol.roots.length === 2 && sol.delta < 0 && sol.roots.every((z) => inView(z)) && (
          <line
            x1={px(sol.roots[0]!).x}
            y1={px(sol.roots[0]!).y}
            x2={px(sol.roots[1]!).x}
            y2={px(sol.roots[1]!).y}
            stroke={RES_COLOR}
            strokeWidth={2}
            strokeDasharray="5 4"
            aria-hidden
          />
        )}
        {sol.roots.map((z, i) => (
          <PointMark
            key={i}
            z={z}
            color={RES_COLOR}
            label={names[i]!}
            // racines réelles : noms au-dessus et au-dessous de l'axe, en alternance
            offset={sol.delta >= 0 ? { dx: 0, dy: i === 0 ? -18 : 18 } : undefined}
            testId={`cp-root-${i}`}
          />
        ))}
      </Plane>

      <Live
        text={`Δ = ${formatNumber(sol.delta)} : ${
          sol.delta < 0
            ? 'deux racines complexes conjuguées'
            : sol.delta > 0
              ? 'deux racines réelles'
              : 'une racine double'
        }`}
      />
      <div className="space-y-2" data-testid="cp-equation">
        <StepList steps={sol.steps} />
        <StepList steps={extra} />
        {onSend && (
          <div className="flex justify-end">
            <SendToSheetButton
              label={formatNumber(sol.delta)}
              cost={sendCost}
              onClick={() =>
                onSend({ atoms: sol.delta < 0 ? ['−', String(-sol.delta)] : [String(sol.delta)] })
              }
            />
          </div>
        )}
        {out && (
          <Note>
            {frenchSpaces(
              'Une racine sort de la figure : on ne la voit pas, mais le calcul reste juste.',
            )}
          </Note>
        )}
        {inView({ a: xs, b: 0 }) && (
          <Note>
            {frenchSpaces(
              sol.delta === 0
                ? 'La racine double est sur l’axe des réels, en −b/(2a).'
                : 'Droite en pointillés : les points de partie réelle −b/(2a), l’axe de symétrie des deux racines.',
            )}
          </Note>
        )}
      </div>
    </div>
  );
}
